import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Mail, Shield, Trash2, Clock, Check, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    role: "employee"
  });

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(`${API}/team/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data);
    } catch (error) {
      console.error("Error loading team:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(`${API}/team/invite`, inviteForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invitación enviada exitosamente");
      setShowInviteDialog(false);
      setInviteForm({ email: "", name: "", role: "employee" });
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar invitación");
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("¿Estás seguro de eliminar este miembro?")) return;
    
    try {
      const token = localStorage.getItem("cia_token");
      await axios.delete(`${API}/team/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Miembro eliminado");
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar miembro");
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      const token = localStorage.getItem("cia_token");
      await axios.put(`${API}/team/members/${memberId}/role?role=${newRole}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Rol actualizado");
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar rol");
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-purple-100 text-purple-800",
      employee: "bg-blue-100 text-blue-800",
      viewer: "bg-slate-100 text-slate-800",
      owner: "bg-yellow-100 text-yellow-800"
    };
    const labels = {
      admin: "Administrador",
      employee: "Empleado",
      viewer: "Solo lectura",
      owner: "Propietario"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[role] || styles.employee}`}>
        {labels[role] || role}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    if (status === "active") {
      return (
        <span className="flex items-center gap-1 text-green-600 text-sm">
          <Check className="w-4 h-4" />
          Activo
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-yellow-600 text-sm">
        <Clock className="w-4 h-4" />
        Pendiente
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#5B9BD5]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Equipo</h1>
          <p className="text-slate-600">Administra los miembros de tu empresa</p>
        </div>
        
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogTrigger asChild>
            <Button className="cia-btn-primary" data-testid="invite-member-btn">
              <Plus className="w-4 h-4 mr-2" />
              Invitar Miembro
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invitar nuevo miembro</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  placeholder="Nombre del colaborador"
                  required
                  data-testid="invite-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Correo electrónico</Label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  required
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select 
                  value={inviteForm.role} 
                  onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                >
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador (acceso total)</SelectItem>
                    <SelectItem value="employee">Empleado (crear facturas)</SelectItem>
                    <SelectItem value="viewer">Solo lectura</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full cia-btn-primary" disabled={inviting}>
                {inviting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Enviar Invitación
                  </>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Team Members List */}
      <Card className="cia-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#5B9BD5]" />
            Miembros del Equipo ({members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No hay miembros en el equipo</p>
              <p className="text-sm text-slate-400">Invita a tus colaboradores para comenzar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {members.map((member) => (
                <div 
                  key={member.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  data-testid={`team-member-${member.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#5B9BD5] flex items-center justify-center text-white font-bold">
                      {member.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{member.name}</p>
                      <p className="text-sm text-slate-500">{member.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {getStatusBadge(member.status)}
                    
                    <Select 
                      value={member.role}
                      onValueChange={(value) => handleUpdateRole(member.id, value)}
                      disabled={member.role === "owner"}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrador</SelectItem>
                        <SelectItem value="employee">Empleado</SelectItem>
                        <SelectItem value="viewer">Solo lectura</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {member.role !== "owner" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissions Info */}
      <Card className="cia-card border-blue-200 bg-blue-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-800 mb-2">Permisos por Rol</h3>
              <ul className="text-blue-700 text-sm space-y-1">
                <li><strong>Administrador:</strong> Acceso total, puede invitar/eliminar miembros</li>
                <li><strong>Empleado:</strong> Crear facturas, gestionar clientes, ver reportes</li>
                <li><strong>Solo lectura:</strong> Ver facturas y reportes, sin edición</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamPage;
